/**
 * @fileoverview A bed's plants growing now, and the way to add more: each plant a row with its photo, stage and week
 * (LocationPlantList), then "Add a new plant here", which opens the Plants pop-up with this bed preset (D78 P2,
 * decision 6): "Take or upload a photo" (a photo of the bed's seed packets is read as packets, so the old "Add plants
 * from seed packets" row folds into it) or "Type it in" (the plant form with this bed chosen).
 *
 * Taken out of the bed's card (D56) so the open bed (/beds/:id, BedOpen) shows it under the bed's week, as its
 * Plants, and the card drawn below it does not show it twice; the bed's card still draws it in its own Plants fold
 * elsewhere.
 *
 * Last in the list, while the bed has plants growing, is the row "Clear the bed for the season..." (B158 job 4; it was in the
 * card's ⋯ menu): a DoorRow that calls onClearAll, which opens the same Clear panel as before.
 *
 * Where: LocationCard (its Plants fold), BedOpen (the open bed's Plants).
 * API: none of its own (the pop-up is LocationGrid's useAddPlant).
 * Words (en/es): locations.card.plants.list.addNew, locations.bed.clearMenu.
 *
 * @module BedPlants
 */

import React from 'react';
import { Archive, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { Location, LocationPlant } from '@/shared/api/locations';
import { DoorRow } from '@/shared/components/ui/DoorRow';
import { LocationPlantList } from './LocationPlantList';

/** What the plants section draws. */
interface BedPlantsProps {
  /** The bed. */
  location: Location;
  /** Its plants growing now. */
  plants: LocationPlant[];
  /** Opens one of them, full size. */
  onOpenPlant: (plantObjId: number) => void;
  /** Opens the Plants pop-up with this bed preset; none, no "Add a new plant here". */
  onAddPlant?: () => void;
  /** Opens the Clear panel (archives the plants growing here); none, no "Clear the bed for the season...". */
  onClearAll?: () => void;
  /** An archived bed: rows only, nothing to add. */
  readOnly?: boolean;
}

/**
 * The bed's plants and the way to add more.
 *
 * @param props - BedPlantsProps.
 */
export const BedPlants: React.FC<BedPlantsProps> = ({ plants, onOpenPlant, onAddPlant, onClearAll, readOnly = false }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <>
      <LocationPlantList plants={plants} onOpenPlant={onOpenPlant} />
      {/* A new plant, with this location already chosen: by a photo or typed. */}
      {onAddPlant && !readOnly && (
        <button
          type="button"
          onClick={onAddPlant}
          className={cn('flex min-h-12 w-full items-center gap-3 px-2 text-left text-sm font-semibold text-primary', plants.length > 0 && 'border-t')}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border-[1.5px] border-dashed border-current" aria-hidden="true">
            <Plus className="h-4 w-4" />
          </span>
          {t('locations.card.plants.list.addNew')}
        </button>
      )}
      {/* The end-of-season job for every plant here, last (B158): the ordinary ink, the same panel as before. */}
      {onClearAll && !readOnly && plants.length > 0 && (
        <DoorRow icon={<Archive />} onClick={onClearAll} className="border-t rounded-none">{t('locations.bed.clearMenu')}</DoorRow>
      )}
    </>
  );
};
